import { useEffect, useState } from "react";
import { api } from "../../lib/api";

const SOURCE_STYLES = {
  "Direct": "bg-slate-100 text-slate-700 border-slate-200",
  "Google Search": "bg-blue-50 text-blue-700 border-blue-200",
  "Search": "bg-blue-50 text-blue-700 border-blue-200",
  "Ads": "bg-amber-50 text-amber-800 border-amber-200",
  "Social": "bg-pink-50 text-pink-700 border-pink-200",
  "Referral": "bg-violet-50 text-violet-700 border-violet-200",
  "Email": "bg-teal-50 text-teal-700 border-teal-200",
  "Campaign": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function rel(ts) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function SourceTag({ source }) {
  const cls = SOURCE_STYLES[source] || SOURCE_STYLES["Direct"];
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid="journey-source">
      {source}
    </span>
  );
}

function JourneyCard({ j }) {
  return (
    <div className="p-4" data-testid="journey-card">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          {j.active && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Active now" />}
          <SourceTag source={j.source} />
          <span className="text-sm font-medium text-[#12263A]" data-testid="journey-location">📍 {j.location}</span>
        </div>
        <div className="text-xs text-[#6B7280]" data-testid="journey-meta">
          {j.page_count} {j.page_count === 1 ? "page" : "pages"} · {rel(j.last_ts)}
        </div>
      </div>
      {j.campaign && (
        <div className="text-xs text-[#6B7280] mb-1.5">Campaign: <span className="font-medium">{j.campaign}</span></div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="journey-path">
        {j.pages.map((p, i) => (
          <span key={`${p.ts}-${i}`} className="flex items-center gap-1.5">
            <span className="font-mono text-xs bg-[#F3F4F6] text-[#12263A] px-1.5 py-0.5 rounded">{p.path}</span>
            {i < j.pages.length - 1 && <span className="text-[#9CA3AF] text-xs">→</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function LivePanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/analytics/live").then(r => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return <div className="label-eyebrow">Loading…</div>;
  const journeys = data.journeys || [];
  return (
    <div data-testid="live-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <div className="border border-[#E5E7EB] bg-white p-5">
          <div className="label-eyebrow flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Active now</div>
          <div className="text-4xl font-black" data-testid="live-active">{data.active_now}</div>
          <div className="text-xs text-[#6B7280]">visitors in the last 5 minutes</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white p-5">
          <div className="label-eyebrow">Today's visitors</div>
          <div className="text-4xl font-black" data-testid="live-today-visitors">{data.today_visitors}</div>
          <div className="text-xs text-[#6B7280]">unique sessions today</div>
        </div>
        <div className="border border-[#E5E7EB] bg-white p-5">
          <div className="label-eyebrow">Today's page views</div>
          <div className="text-4xl font-black" data-testid="live-today-views">{data.today_views}</div>
          <div className="text-xs text-[#6B7280]">total views today</div>
        </div>
      </div>
      <h2 className="text-xl font-bold tracking-tight mb-3">Live visitor journeys</h2>
      <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]" data-testid="live-feed">
        {journeys.length === 0 && <div className="p-5 text-sm text-[#6B7280]">No activity yet.</div>}
        {journeys.map((j) => (
          <JourneyCard key={j.session_id} j={j} />
        ))}
      </div>
    </div>
  );
}
